/**
 * Browser-side ReplayEngine — runs entirely in the browser.
 * Ported from the server-side version with NodeJS.Timeout → ReturnType<typeof setTimeout>.
 */

import {
  normalizeReplayFile,
  type ReplayEntry,
  type ReplayFile,
  type ReplayMeta,
  type ServerMessage,
} from "./types.js";

export class ReplayEngine {
  private entries: ReplayEntry[];
  private meta: ReplayMeta;
  private speed: number;
  private paused = false;
  private currentIndex = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private pausedAtTs = 0;
  private replayStartRealMs = 0;
  private stopped = false;
  private demoMode = true;
  private demoPacedPrefixCounts: number[] = [0];
  private demoPacedEntryCount = 0;

  constructor(
    replayFile: ReplayFile,
    private emit: (msg: ServerMessage) => void,
    speed = 4,
  ) {
    const normalized = normalizeReplayFile(replayFile);
    this.entries = normalized.entries;
    this.meta = normalized.meta;
    this.speed = speed;
    for (const entry of this.entries) {
      const previous = this.demoPacedPrefixCounts[this.demoPacedPrefixCounts.length - 1] ?? 0;
      this.demoPacedPrefixCounts.push(previous + (this.isDemoPacedEntry(entry.msg) ? 1 : 0));
    }
    this.demoPacedEntryCount =
      this.demoPacedPrefixCounts[this.demoPacedPrefixCounts.length - 1] ?? 0;
  }

  start() {
    this.stopped = false;
    this.replayStartRealMs = Date.now();
    this.emit({
      type: "replay-start",
      meta: this.meta,
      demoMode: this.demoMode,
    });
    this.scheduleNext();
  }

  stop() {
    this.stopped = true;
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    this.emit({ type: "replay-end" });
  }

  pause() {
    if (this.paused) return;
    this.paused = true;
    this.pausedAtTs = this.currentReplayTs();
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  resume() {
    if (!this.paused) return;
    this.paused = false;
    this.replayStartRealMs = Date.now() - (this.pausedAtTs / this.speed);
    this.scheduleNext();
  }

  setSpeed(newSpeed: number) {
    const currentTs = this.currentReplayTs();
    this.speed = newSpeed;
    this.replayStartRealMs = Date.now() - (currentTs / this.speed);
    if (!this.paused && this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
      this.scheduleNext();
    }
  }

  setDemoMode(enabled: boolean) {
    if (this.demoMode === enabled) return;
    this.demoMode = enabled;
    const nextTs = this.demoMode
      ? this.demoReplayTsForIndex(this.currentIndex)
      : this.timelineReplayTsForIndex(this.currentIndex);
    this.replayStartRealMs = Date.now() - (nextTs / this.speed);
    this.pausedAtTs = nextTs;
    if (!this.paused && this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
      this.scheduleNext();
    }
    this.broadcastProgress(nextTs);
  }

  seek(position: number) {
    this.emit({ type: "replay-seek" });

    if (this.demoMode) {
      const targetPacedCount = Math.round(position * this.demoTotalSteps());
      this.currentIndex = 0;
      while (
        this.currentIndex < this.entries.length &&
        this.demoPacedCountAfterIndex(this.currentIndex) <= targetPacedCount
      ) {
        this.emit(this.entries[this.currentIndex].msg);
        this.currentIndex++;
      }
      const virtualTs = this.demoReplayTsForPacedCount(targetPacedCount);
      this.replayStartRealMs = Date.now() - (virtualTs / this.speed);
      this.pausedAtTs = virtualTs;
      this.broadcastProgress(virtualTs);
    } else {
      const targetTs = position * this.meta.totalDurationMs;
      this.currentIndex = 0;
      while (
        this.currentIndex < this.entries.length &&
        this.entries[this.currentIndex].replayTs <= targetTs
      ) {
        this.emit(this.entries[this.currentIndex].msg);
        this.currentIndex++;
      }
      this.replayStartRealMs = Date.now() - (targetTs / this.speed);
      this.pausedAtTs = targetTs;
      this.broadcastProgress(targetTs);
    }

    if (!this.paused) {
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
      }
      this.scheduleNext();
    }
  }

  private currentReplayTs(): number {
    if (this.paused) return this.pausedAtTs;
    return (Date.now() - this.replayStartRealMs) * this.speed;
  }

  private isDemoPacedEntry(msg: ServerMessage): boolean {
    return (
      msg.type === "event" ||
      msg.type === "result" ||
      msg.type === "comparison" ||
      msg.type === "error"
    );
  }

  private demoTotalSteps(): number {
    return this.demoPacedEntryCount > 0 ? this.demoPacedEntryCount : this.entries.length;
  }

  private demoPacedCountAtIndex(index: number): number {
    if (this.demoPacedEntryCount > 0) {
      return this.demoPacedPrefixCounts[index] ?? this.demoPacedEntryCount;
    }
    return index;
  }

  private demoPacedCountAfterIndex(index: number): number {
    if (this.demoPacedEntryCount > 0) {
      return this.demoPacedPrefixCounts[index + 1] ?? this.demoPacedEntryCount;
    }
    return index + 1;
  }

  private demoReplayTsForPacedCount(pacedCount: number): number {
    const totalSteps = this.demoTotalSteps();
    if (totalSteps <= 0) return 0;
    const clamped = Math.max(0, Math.min(totalSteps, pacedCount));
    return (clamped / totalSteps) * this.meta.totalDurationMs;
  }

  private demoReplayTsForIndex(index: number): number {
    return this.demoReplayTsForPacedCount(this.demoPacedCountAtIndex(index));
  }

  private timelineReplayTsForIndex(index: number): number {
    if (index <= 0) return 0;
    if (index >= this.entries.length) return this.meta.totalDurationMs;
    return this.entries[index]?.replayTs ?? this.meta.totalDurationMs;
  }

  private scheduleNext() {
    if (this.stopped || this.paused) return;
    if (this.currentIndex >= this.entries.length) {
      this.broadcastProgress(this.meta.totalDurationMs);
      this.emit({ type: "replay-end" });
      return;
    }

    const entry = this.entries[this.currentIndex];
    const currentReplayTs = this.currentReplayTs();
    const targetTs = this.demoMode
      ? this.demoReplayTsForIndex(this.currentIndex)
      : entry.replayTs;
    const delayMs = Math.max(0, (targetTs - currentReplayTs) / this.speed);

    this.timer = setTimeout(() => {
      if (this.stopped) return;
      this.emit(entry.msg);
      this.currentIndex++;

      if (this.currentIndex % 10 === 0 || this.currentIndex >= this.entries.length) {
        const progressTs = this.demoMode
          ? this.demoReplayTsForIndex(this.currentIndex)
          : entry.replayTs;
        this.broadcastProgress(progressTs);
      }

      this.scheduleNext();
    }, delayMs);
  }

  private broadcastProgress(replayTs: number) {
    const position = this.meta.totalDurationMs > 0
      ? Math.min(1, replayTs / this.meta.totalDurationMs)
      : 1;
    this.emit({
      type: "replay-progress",
      position,
      elapsed: Math.round(replayTs),
      total: this.meta.totalDurationMs,
      speed: this.speed,
      paused: this.paused,
      demoMode: this.demoMode,
    });
  }
}
